import React, { Fragment, memo, useEffect, useMemo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import {
  ActivatePartnerDriver,
  DeactivatePartnerDriver,
  RejectOnboarding,
  UnrejectOnboarding,
  SendRemainderContractor,
  SendRemainderOnboarding,
  UpdatePartnerDriverAutomaticReactivation,
} from 'src/constants/actionTypes'
import { ContractorDetails } from 'src/interfaces/contractor'
import { OnboardingStatus } from 'src/constants/onboarding/enums'
import { CollectedDataStatus, ContractorStepExpirationStatus } from 'src/constants/collectedData'
import { RejectContractor } from './RejectContractor'
import { UnrejectContractor } from './UnrejectContractor'
import { ActivateContractor } from './ActivateContractor'
import { DeactivateContractor } from './DeactivateContractor'
import { SetDeactivationDate } from './SetDeactivationDate'
import { ResendInvite } from './ResendInvite'
import { ResendRecollection } from './ResendRecollection'
import type { ResendInviteKind } from './ResendInvite.types'
import type { SendReminderResponse } from 'src/interfaces/reminder'
import { colors } from 'src/theme'
import { ContractorDeactivationReason } from 'src/constants/contractor/enums'
import { Tooltip, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { IOSSwitch } from 'src/components/shared/IOSSwitch'
import { GSIcon } from 'src/components/shared/GSIcon'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface ContractorActionsProps {
  contractorDetails: ContractorDetails
  flow: 'onboardings' | 'contractors'
  isSubcontractor?: boolean
  onRefresh(): Promise<void> | void
  onClose(): void
}

const ContractorActions = memo(
  ({ contractorDetails, flow, onRefresh, onClose, isSubcontractor }: ContractorActionsProps) => {
    const dispatch = useDispatch()

    const { hasPermission } = useCheckPermissions()

    const canEditPartnerDriver = hasPermission(UserPermission.PartnerDriversEdit)

    const [isLoading, setIsLoading] = useState(false)
    const [isAutomaticReactivationEnabled, setIsAutomaticReactivationEnabled] = useState(
      !!contractorDetails.autoActivationEnabled,
    )

    useEffect(() => {
      setIsAutomaticReactivationEnabled(!!contractorDetails.autoActivationEnabled)
    }, [contractorDetails.autoActivationEnabled])

    const hasStepsWithRecollectingStatus = contractorDetails.collectedData.some(
      (item) => !!item.isRecollecting,
    )

    const hasStepsWithExpiredStatus = contractorDetails.collectedData.some(
      (item) =>
        item.status === CollectedDataStatus.Expired ||
        item.expirationStatus === ContractorStepExpirationStatus.Expired,
    )

    const resendKind = useMemo((): ResendInviteKind | null => {
      if (isSubcontractor) {
        return null
      }

      const { collectedDataStatus, deactivationReason, onboardedAt } = contractorDetails

      if (
        !!onboardedAt &&
        deactivationReason !== ContractorDeactivationReason.Manual &&
        (hasStepsWithRecollectingStatus || hasStepsWithExpiredStatus) &&
        flow === 'contractors'
      ) {
        return 'recollection'
      }

      if (
        collectedDataStatus === OnboardingStatus.Created ||
        collectedDataStatus === OnboardingStatus.PartiallyCompleted
      ) {
        return 'invite'
      }

      return null
    }, [
      isSubcontractor,
      contractorDetails,
      hasStepsWithRecollectingStatus,
      hasStepsWithExpiredStatus,
      flow,
    ])

    const handleRejectWorker = useCallback(async () => {
      setIsLoading(true)
      try {
        await createAsyncAction(
          dispatch,
          RejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
        )
        showToast('Contractor rejected successfully!')
        await onRefresh()

        onClose()
      } catch (err) {
        handleApiError(err)
        showToast('Failed to reject contractor!')
      } finally {
        setIsLoading(false)
      }
    }, [contractorDetails, onRefresh])

    const handleUnrejectWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          UnrejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
        )
        showToast('Contractor unrejected!')
        await onRefresh()
        onClose()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleActivateWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          ActivatePartnerDriver.request({
            partnerDriverId: contractorDetails.id,
          }),
        )
        showToast('Contractor activated!')
        onRefresh()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleDeactivateWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          DeactivatePartnerDriver.request({
            partnerDriverId: contractorDetails.id,
          }),
        )
        showToast('Contractor deactivated!')
        onRefresh()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleResendInvite = useCallback(async (): Promise<SendReminderResponse | null> => {
      if (!resendKind) {
        return null
      }

      const resendAction =
        resendKind === 'invite' ? SendRemainderOnboarding : SendRemainderContractor

      try {
        const response: SendReminderResponse = await createAsyncAction(
          dispatch,
          resendAction.request({
            partnerDriverId: contractorDetails.id,
          }),
        )

        showToast(
          "The reminder has been resent to the contractor's email address and phone number!",
        )

        return response
      } catch (err) {
        handleApiError(err)
        showToast('Failed to resend the reminder.')

        onRefresh()

        return null
      }
    }, [contractorDetails.id, dispatch, onRefresh, resendKind])

    const contractorFullName = `${contractorDetails.firstName ?? ''} ${
      contractorDetails.lastName ?? ''
    }`.trim()

    const handleAutomaticReactivationChange = useCallback(async () => {
      const previousValue = isAutomaticReactivationEnabled
      const nextValue = !previousValue

      setIsAutomaticReactivationEnabled(nextValue)

      try {
        const response: { autoActivationEnabled: boolean } = await createAsyncAction(
          dispatch,
          UpdatePartnerDriverAutomaticReactivation.request({
            partnerDriverId: contractorDetails.id,
            autoActivationEnabled: nextValue,
          }),
        )

        setIsAutomaticReactivationEnabled(!!response.autoActivationEnabled)
      } catch (error) {
        setIsAutomaticReactivationEnabled(previousValue)

        handleApiError(error)

        return
      }

      try {
        await onRefresh()
      } catch (error) {
        handleApiError(error)
      }
    }, [contractorDetails.id, dispatch, isAutomaticReactivationEnabled, onRefresh])

    const mainActionButton = useMemo(() => {
      if (contractorDetails.collectedDataStatus === OnboardingStatus.Failed) {
        return <UnrejectContractor onUnreject={handleUnrejectWorker} />
      } else if (flow === 'onboardings') {
        if (contractorDetails.collectedDataStatus !== OnboardingStatus.Completed) {
          return <RejectContractor onReject={handleRejectWorker} isLoading={isLoading} />
        }
      } else if (canEditPartnerDriver) {
        if (contractorDetails.isActive) {
          return <DeactivateContractor onDeactivate={handleDeactivateWorker} />
        } else {
          return <ActivateContractor onActivate={handleActivateWorker} />
        }
      }

      return null
    }, [
      contractorDetails.collectedDataStatus,
      contractorDetails.isActive,
      flow,
      isLoading,
      canEditPartnerDriver,
      handleUnrejectWorker,
      handleRejectWorker,
      handleActivateWorker,
      handleDeactivateWorker,
    ])

    return (
      <div data-testid="contractor-details-actions" css={tw`flex flex-col p-4 pb-4`}>
        <div css={tw`w-full h-px bg-[#EDEDED] mb-4`} />
        {contractorDetails.isActive ? (
          <Fragment>
            <div css={tw`flex flex-row items-center justify-between gap-4 mb-4`}>
              <div css={tw`flex flex-col min-w-0`}>
                <Typography variant="h6" css={tw`font-semibold text-black`}>
                  Deactivation date
                </Typography>
                <Typography variant="body2" css={tw`text-[#655E7C]`}>
                  {contractorDetails.deactivationScheduledAt
                    ? dayjs(contractorDetails.deactivationScheduledAt).format('MMM D, YYYY')
                    : 'Not set'}
                </Typography>
              </div>
              <SetDeactivationDate contractorDetails={contractorDetails} onRefresh={onRefresh} />
            </div>
            <div css={tw`w-full h-px bg-[#EDEDED] mb-4`} />
          </Fragment>
        ) : null}
        {flow === 'contractors' && canEditPartnerDriver && (
          <Fragment>
            <div css={tw`flex flex-row items-center justify-between gap-4 mb-4`}>
              <div css={tw`flex flex-col min-w-0`}>
                <div css={tw`flex flex-row items-center`}>
                  <Typography variant="h6" css={tw`font-semibold text-black`}>
                    Automatic Re-Activation
                  </Typography>
                  <Tooltip
                    color="black"
                    title={
                      <div css={tw`pb-1`}>
                        <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                          Auto Re-Activation
                        </Typography>
                        <Typography variant="body2" color={colors.SHADES_GREY_300}>
                          When enabled, contractors are automatically reactivated once they become
                          compliant again, regardless of how they were deactivated.
                        </Typography>
                        <Typography variant="body2" color={colors.SHADES_GREY_300} css={tw`mt-2`}>
                          When disabled, contractors remain in their current deactivated state even
                          after becoming compliant.
                        </Typography>
                      </div>
                    }
                    placement="right-end"
                  >
                    <span css={tw`h-5 cursor-pointer flex items-center ml-1.5`}>
                      <GSIcon iconName="gs_icon_circle_info_2" width={20} height={20} />
                    </span>
                  </Tooltip>
                </div>
                <Typography
                  variant="body2"
                  color={
                    isAutomaticReactivationEnabled ? colors.SHADES_GREY_400 : colors.COLORS_RED_500
                  }
                >
                  {isAutomaticReactivationEnabled ? 'Enabled' : 'Disabled'}
                </Typography>
              </div>
              <IOSSwitch
                checked={isAutomaticReactivationEnabled}
                inputProps={
                  {
                    'aria-label': 'Automatic re-activation',
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
                onChange={handleAutomaticReactivationChange}
              />
            </div>
            <div css={tw`w-full h-px bg-[#EDEDED] mb-4`} />
          </Fragment>
        )}
        {mainActionButton}
        {resendKind === 'invite' && (
          <ResendInvite
            contractorName={contractorFullName}
            resendAvailableAt={contractorDetails.resendAvailableAt}
            onResend={handleResendInvite}
          />
        )}
        {resendKind === 'recollection' && (
          <ResendRecollection
            contractorName={contractorFullName}
            resendAvailableAt={contractorDetails.resendAvailableAt}
            onResend={handleResendInvite}
          />
        )}
      </div>
    )
  },
)

export { ContractorActions }
